import type { BoardLayer, KeyboardLayout } from "@/domain/types";
import { defaultBoardLayer, type ImportedKeyboard } from "@/hardware/keyboards/keyboard-import";
import { getLayout, qwerty, universalLayouts } from "./index";

export interface LayoutOption {
  id: string;
  name: string;
}

export interface LayoutGroup {
  label: string;
  options: LayoutOption[];
}

const opt = (l: { id: string; name: string }): LayoutOption => ({ id: l.id, name: l.name });

/**
 * What the Keyboard-Layout picker offers for the active board: a custom board's
 * own board layers replace the universal set; a single-layer (KLE) board keeps its bundled
 * legends and may override with a universal mapping; empty board layers are hidden.
 */
export function layoutGroups(imported: ImportedKeyboard | undefined): LayoutGroup[] {
  const universal: LayoutGroup = { label: "Universal", options: universalLayouts.map(opt) };
  if (!imported) return [universal];
  const own = imported.boardLayers.filter((l) => !l.empty).map(opt);
  if (imported.boardLayers.length === 1) {
    return [{ label: "This board", options: own }, universal];
  }
  return [{ label: "Board layers", options: own }];
}

/**
 * The active mapping for a picker selection. Board layers resolve by id (chars are already
 * selected-over-default resolved at import); unknown or absent selections fall back to the
 * board's default layer, or to QWERTY when no custom board is active.
 */
export function activeLayout(
  imported: ImportedKeyboard | undefined,
  selectedId: string | undefined,
): KeyboardLayout | BoardLayer {
  if (imported) {
    const own = imported.boardLayers.find((l) => l.id === selectedId);
    if (own) return own;
    if (imported.boardLayers.length === 1 && selectedId) {
      const universal = getLayout(selectedId);
      if (universal) return universal;
    }
    const def = defaultBoardLayer(imported);
    if (!def.empty) return def;
    // the configured default is an empty layer (hidden from the picker): activate the
    // first usable own layer instead, or QWERTY when every layer is empty, so we never
    // point the selector at an option it does not show
    return imported.boardLayers.find((l) => !l.empty) ?? qwerty;
  }
  return (selectedId ? getLayout(selectedId) : undefined) ?? qwerty;
}
